<script>
import { useAlert } from 'dashboard/composables';
import ConversationApi from 'dashboard/api/inbox/conversation';
import { mapGetters } from 'vuex';
import ringtoneService from 'dashboard/services/ringtoneService';

export default {
  name: 'VoiceCallButton',
  props: {
    conversationId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      isInCall: false,
      isConnecting: false,
      isMuted: false,
      callStartTime: null,
      callDurationInterval: null,
      room: null,
      localParticipant: null,
      currentTime: new Date(),
      audioElements: new Map(), // Track audio elements for cleanup
      connectionRetries: 0,
      maxRetries: 3,
      isRinging: false,
      currentCallId: null,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentUser: 'getCurrentUser',
    }),
    conversation() {
      let chat = this.currentChat?.id ? this.currentChat : null;

      if (!chat) {
        const allConversations =
          this.$store.state.conversations.allConversations;
        chat = allConversations.find(
          conv => conv.id === Number(this.conversationId)
        );
      }

      return chat || {};
    },
    showVoiceCallButton() {
      return this.conversation?.custom_attributes?.voice_call_active;
    },
    isCallPending() {
      return this.conversation?.custom_attributes?.voice_call_pending;
    },
    // Check if conversation is assigned to current user
    isConversationAssignedToMe() {
      // Get assignee ID from the correct path: conversation.meta.assignee.id
      const assigneeId = this.conversation?.meta?.assignee?.id;

      // Return true if the conversation is assigned to the current user
      return assigneeId === this.currentUser?.id;
    },
    callDuration() {
      if (!this.callStartTime) return '00:00';
      const diff = Math.floor((this.currentTime - this.callStartTime) / 1000);
      const minutes = Math.floor(diff / 60);
      const seconds = diff % 60;
      return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    },
  },

  watch: {
    // Watch for conversation changes to reset state when needed
    'conversation.custom_attributes.voice_call_active': {
      handler(newValue, oldValue) {
        // If voice_call_active changes from false to true (new call coming)
        // and we're not currently in a call, reset the component state
        if (newValue && !oldValue && !this.isInCall) {
          this.resetToInitialState();
        }
        // If voice_call_active becomes false (call ended externally)
        if (!newValue && oldValue) {
          this.resetCallState();
        }
      },
      immediate: false,
    },
    'conversation.custom_attributes.voice_call_pending': {
      handler(newValue, oldValue) {
        // Start ringing when call becomes pending AND conversation is assigned to me
        if (newValue && !oldValue && this.isConversationAssignedToMe) {
          this.startRinging();
        }
        // Stop ringing when call is no longer pending OR not assigned to me
        if (!newValue && oldValue) {
          this.stopRinging();
        }
      },
      immediate: false,
    },
    // Watch for assignment changes
    'conversation.meta.assignee.id': {
      handler(newValue, oldValue) {
        // Stop ringing if conversation is no longer assigned to me
        if (
          oldValue === this.currentUser?.id &&
          newValue !== this.currentUser?.id
        ) {
          this.stopRinging();
        }
        // Start ringing if conversation becomes assigned to me and has pending call
        if (
          newValue === this.currentUser?.id &&
          oldValue !== this.currentUser?.id &&
          this.isCallPending
        ) {
          this.startRinging();
        }
      },
      immediate: false,
    },
    'conversation.custom_attributes': {
      handler() {
        // Component will reactively update
      },
      deep: true,
    },
  },

  mounted() {
    // Preload LiveKit client for better performance
    this.preloadLiveKitClient();

    // Check if there's already a pending call when component mounts AND it's assigned to me
    if (this.isCallPending && this.isConversationAssignedToMe) {
      this.startRinging();
    }
  },

  beforeUnmount() {
    this.cleanup();
    this.stopRinging();
  },

  methods: {
    // Start ringing for incoming call
    startRinging() {
      if (this.isRinging) return;

      // Only ring if conversation is assigned to me
      if (!this.isConversationAssignedToMe) return;

      this.isRinging = true;
      this.currentCallId = `call_${this.conversationId}_${Date.now()}`;

      // Start the ringtone
      ringtoneService.playRingtone(this.currentCallId);
    },

    // Stop ringing
    stopRinging() {
      if (!this.isRinging) return;

      this.isRinging = false;

      // Stop the ringtone
      if (this.currentCallId) {
        ringtoneService.stopRingtoneForCall(this.currentCallId);
        this.currentCallId = null;
      }
    },

    // Reset component to initial state for new calls
    resetToInitialState() {
      // Clean up any existing connections first
      this.cleanup();

      // Reset all state to initial values
      this.isInCall = false;
      this.isConnecting = false;
      this.isMuted = false;
      this.callStartTime = null;
      this.room = null;
      this.localParticipant = null;
      this.currentTime = new Date();
      this.audioElements = new Map();
      this.connectionRetries = 0;

      this.stopCallTimer();
    },

    // Preload LiveKit client to avoid delay during calls
    async preloadLiveKitClient() {
      try {
        if (!window.LivekitClient) {
          await this.loadLiveKitClient();
        }
      } catch (error) {
        useAlert('Failed to preload LiveKit client', 'error');
      }
    },

    async joinVoiceCall() {
      // Prevent multiple simultaneous join attempts
      if (this.isConnecting || this.isInCall) {
        return;
      }

      this.isConnecting = true;
      this.connectionRetries = 0;

      try {
        const response = await ConversationApi.joinVoiceCall(
          this.conversationId
        );

        if (response.data.success) {
          this.isInCall = true;

          // Stop ringing when joining call
          this.stopRinging();

          const joinedAt =
            this.conversation?.custom_attributes?.voice_call_joined_at;
          this.callStartTime = joinedAt ? new Date(joinedAt) : new Date();
          this.currentTime = new Date();
          this.startCallTimer();

          // Connect to LiveKit with retry mechanism
          await this.connectToLiveKitWithRetry(response.data.data);

          useAlert('Successfully joined voice call');
        } else {
          throw new Error('Failed to join voice call');
        }
      } catch (error) {
        this.resetCallState();
        useAlert('Failed to join voice call', 'error');
      } finally {
        this.isConnecting = false;
      }
    },

    async connectToLiveKitWithRetry(voiceData) {
      return this.attemptConnection(voiceData, 0);
    },

    async attemptConnection(voiceData, attemptCount) {
      try {
        await this.connectToLiveKit(voiceData);
        return true; // Success
      } catch (error) {
        this.connectionRetries = attemptCount + 1;
        if (this.connectionRetries >= this.maxRetries) {
          throw error;
        }
        // Wait before retry
        await new Promise(resolve => {
          setTimeout(resolve, 1000 * this.connectionRetries);
        });
        // Recursive retry
        return this.attemptConnection(voiceData, this.connectionRetries);
      }
    },

    async connectToLiveKit(voiceData) {
      try {
        if (!window.LivekitClient) {
          await this.loadLiveKitClient();
        }

        this.room = new window.LivekitClient.Room({
          adaptiveStream: true,
          dynacast: true,
          // Add connection quality settings
          videoCaptureDefaults: {
            resolution: window.LivekitClient.VideoPresets.h720.resolution,
          },
          publishDefaults: { videoEncoding: { maxBitrate: 1500000 } },
        });

        this.setupRoomEventListeners();

        // Determine LiveKit URL
        let livekitUrl = 'wss://quant-vinay-vavy5rb0.livekit.cloud';
        if (voiceData?.room_url) {
          try {
            const url = new URL(voiceData.room_url);
            livekitUrl = `wss://${url.host}`;
          } catch (error) {
            useAlert('Failed to parse room URL, using default');
          }
        }

        // Connect to room
        await this.room.connect(livekitUrl, voiceData.access_token);
        this.localParticipant = this.room.localParticipant;

        // Enable microphone with improved error handling
        await this.setupMicrophone();
      } catch (error) {
        useAlert('LiveKit connection failed', 'error');
        throw error;
      }
    },

    setupRoomEventListeners() {
      // Handle participant connections
      this.room.on(
        window.LivekitClient.RoomEvent.ParticipantConnected,
        participant => {
          this.handleParticipantConnected(participant);
        }
      );

      // Handle track subscriptions with improved audio handling
      this.room.on(
        window.LivekitClient.RoomEvent.TrackSubscribed,
        (track, publication, participant) => {
          this.handleTrackSubscribed(track, publication, participant);
        }
      );

      // Handle track unsubscriptions for cleanup
      this.room.on(
        window.LivekitClient.RoomEvent.TrackUnsubscribed,
        (track, publication, participant) => {
          this.handleTrackUnsubscribed(track, publication, participant);
        }
      );

      // Handle participant disconnections
      this.room.on(
        window.LivekitClient.RoomEvent.ParticipantDisconnected,
        participant => {
          this.cleanupParticipantAudio(participant);
        }
      );

      // Handle connection state changes
      this.room.on(
        window.LivekitClient.RoomEvent.ConnectionStateChanged,
        state => {
          if (state === window.LivekitClient.ConnectionState.Disconnected) {
            this.resetCallState();
          }
        }
      );

      // Handle reconnection
      this.room.on(window.LivekitClient.RoomEvent.Reconnected, () => {
        // Re-setup microphone after reconnection
        this.setupMicrophone().catch(() => {
          useAlert('Failed to setup microphone', 'error');
        });
      });
    },

    async setupMicrophone() {
      try {
        // Get media stream with better constraints
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            sampleRate: 48000,
          },
          video: false,
        });

        const audioTrack = stream.getAudioTracks()[0];
        if (audioTrack && this.localParticipant) {
          // Publish the track
          await this.localParticipant.publishTrack(audioTrack, {
            name: 'microphone',
            source: window.LivekitClient.Track.Source.Microphone,
          });

          // Ensure microphone is enabled
          await this.localParticipant.setMicrophoneEnabled(true);
          this.isMuted = false;
        } else {
          throw new Error('No audio track available');
        }
      } catch (micError) {
        useAlert('Microphone setup failed', 'error');
      }
    },

    handleParticipantConnected(participant) {
      // Subscribe to existing tracks when participant connects
      participant.tracks.forEach(publication => {
        if (publication.isSubscribed && publication.track) {
          this.handleTrackSubscribed(
            publication.track,
            publication,
            participant
          );
        }
      });
    },

    handleTrackSubscribed(track, publication, participant) {
      if (participant.identity === this.localParticipant?.identity) {
        return; // Skip our own tracks
      }

      if (track.kind === 'audio') {
        // Create and setup audio element
        const audioElement = document.createElement('audio');
        audioElement.autoplay = true;
        audioElement.muted = false;
        audioElement.volume = 1.0;

        // Add to DOM (required for some browsers)
        audioElement.style.display = 'none';
        document.body.appendChild(audioElement);

        // Attach track to audio element
        track.attach(audioElement);

        // Store reference for cleanup
        const participantId = participant.identity;
        if (!this.audioElements.has(participantId)) {
          this.audioElements.set(participantId, new Set());
        }
        this.audioElements.get(participantId).add(audioElement);
      }
    },

    handleTrackUnsubscribed(track, publication, participant) {
      if (track.kind === 'audio') {
        this.cleanupParticipantAudio(participant);
      }
    },

    cleanupParticipantAudio(participant) {
      const participantId = participant.identity;
      const elements = this.audioElements.get(participantId);

      if (elements) {
        elements.forEach(audioElement => {
          audioElement.remove();
        });
        this.audioElements.delete(participantId);
      }
    },

    async loadLiveKitClient() {
      return new Promise((resolve, reject) => {
        if (window.LivekitClient) {
          resolve();
          return;
        }

        const script = document.createElement('script');
        script.src =
          'https://cdn.jsdelivr.net/npm/livekit-client/dist/livekit-client.umd.min.js';

        const timeout = setTimeout(() => {
          reject(new Error('LiveKit client load timeout'));
        }, 10000);

        script.onload = () => {
          clearTimeout(timeout);
          // Give a moment for the library to initialize
          setTimeout(() => {
            if (window.LivekitClient) {
              resolve();
            } else {
              reject(new Error('LiveKit client not available after load'));
            }
          }, 100);
        };

        script.onerror = () => {
          clearTimeout(timeout);
          reject(new Error('Failed to load LiveKit client'));
        };

        document.head.appendChild(script);
      });
    },

    async declineVoiceCall() {
      // Prevent multiple decline attempts
      if (this.isConnecting) {
        return;
      }

      try {
        const response = await ConversationApi.declineVoiceCall(
          this.conversationId
        );

        if (response.data.success) {
          // Stop ringing when declining call
          this.stopRinging();

          // Reset state after declining
          this.resetToInitialState();
          useAlert('Voice call declined');
        } else {
          throw new Error('Failed to decline voice call');
        }
      } catch (error) {
        useAlert('Failed to decline voice call', 'error');
      }
    },

    async endVoiceCall() {
      try {
        // Disconnect from LiveKit first
        if (this.room) {
          await this.room.disconnect();
        }

        const response = await ConversationApi.endVoiceCall(
          this.conversationId
        );

        if (response.data.success) {
          this.resetCallState();
          useAlert('Voice call ended');
        } else {
          throw new Error('Failed to end voice call');
        }
      } catch (error) {
        useAlert('Failed to end voice call', 'error');
      }
    },

    async toggleMute() {
      if (!this.localParticipant || !this.isInCall) return;

      try {
        const newMutedState = !this.isMuted;
        await this.localParticipant.setMicrophoneEnabled(!newMutedState);

        this.isMuted = newMutedState;
        useAlert(this.isMuted ? 'Microphone muted' : 'Microphone unmuted');
      } catch (error) {
        useAlert('Failed to toggle microphone', 'error');
      }
    },

    startCallTimer() {
      if (this.callDurationInterval) {
        clearInterval(this.callDurationInterval);
      }

      this.callDurationInterval = setInterval(() => {
        if (this.isInCall) {
          this.currentTime = new Date();
        } else {
          this.stopCallTimer();
        }
      }, 1000);
    },

    stopCallTimer() {
      if (this.callDurationInterval) {
        clearInterval(this.callDurationInterval);
        this.callDurationInterval = null;
      }
    },

    resetCallState() {
      this.isInCall = false;
      this.isConnecting = false;
      this.isMuted = false;
      this.callStartTime = null;
      this.stopCallTimer();
      this.stopRinging();
      this.cleanup();
    },

    cleanup() {
      // Clean up audio elements
      this.audioElements.forEach(elements => {
        elements.forEach(audioElement => {
          try {
            audioElement.remove();
          } catch (e) {
            // Element might already be removed
          }
        });
      });
      this.audioElements.clear();

      // Clean up LiveKit room
      if (this.room) {
        try {
          this.room.disconnect();
        } catch (e) {
          // Room might already be disconnected
        }
        this.room = null;
        this.localParticipant = null;
      }

      this.stopCallTimer();
      this.connectionRetries = 0;
    },
  },
};
</script>

<template>
  <div class="voice-call-button">
    <!-- Voice Call Options (when call arrives) - Only for assigned agent -->
    <div
      v-if="showVoiceCallButton && !isInCall && isConversationAssignedToMe"
      class="call-options"
      :class="{ 'incoming-call': isCallPending }"
    >
      <button
        class="control-btn join-btn"
        :disabled="isConnecting"
        @click="joinVoiceCall"
      >
        <svg
          v-if="!isConnecting"
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          class="mr-1"
        >
          <path
            d="M12.5 3.33333V7.5M12.5 7.5L16.6667 7.5M12.5 7.5L17.5 2.5M8.52253 11.5526C7.52121 10.5512 6.73055 9.41904 6.15056 8.21102C6.10067 8.10712 6.07572 8.05516 6.05656 7.98942C5.98845 7.75579 6.03737 7.46891 6.17905 7.27105C6.21892 7.21537 6.26655 7.16774 6.36181 7.07248C6.65315 6.78115 6.79881 6.63548 6.89405 6.48899C7.25321 5.93659 7.25321 5.22443 6.89405 4.67203C6.79881 4.52554 6.65315 4.37988 6.36181 4.08854L6.19942 3.92615C5.75655 3.48328 5.53511 3.26184 5.29729 3.14156C4.82433 2.90233 4.26577 2.90233 3.79281 3.14156C3.55499 3.26184 3.33355 3.48328 2.89069 3.92615L2.75932 4.05751C2.31797 4.49886 2.09729 4.71954 1.92875 5.01956C1.74174 5.35249 1.60727 5.86956 1.60841 6.25141C1.60943 6.59554 1.67618 6.83072 1.80969 7.30109C2.52716 9.82892 3.88089 12.2142 5.87088 14.2042C7.86086 16.1942 10.2462 17.5479 12.774 18.2654C13.2444 18.3989 13.4795 18.4657 13.8237 18.4667C14.2055 18.4678 14.7226 18.3333 15.0555 18.1463C15.3555 17.9778 15.5762 17.7571 16.0176 17.3158L16.1489 17.1844C16.5918 16.7415 16.8132 16.5201 16.9335 16.2823C17.1728 15.8093 17.1728 15.2508 16.9335 14.7778C16.8132 14.54 16.5918 14.3185 16.1489 13.8757L15.9865 13.7133C15.6952 13.4219 15.5495 13.2763 15.4031 13.181C14.8506 12.8219 14.1385 12.8219 13.5861 13.181C13.4396 13.2763 13.2939 13.4219 13.0026 13.7133C12.9073 13.8085 12.8597 13.8562 12.804 13.896C12.6062 14.0377 12.3193 14.0866 12.0857 14.0185C12.0199 13.9994 11.968 13.9744 11.8641 13.9245C10.656 13.3445 9.52384 12.5539 8.52253 11.5526Z"
            stroke="currentColor"
            stroke-width="1.66667"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <span v-if="isConnecting">{{ $t('VOICE_CALL.CONNECTING') }}</span>

        <span v-else>{{ $t('VOICE_CALL.JOIN_CALL') }}</span>
      </button>

      <button
        class="control-btn decline-btn"
        :disabled="isConnecting"
        @click="declineVoiceCall"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          class="mr-1"
        >
          <path
            d="M4.56285 10.7193C3.70374 9.38529 3.06369 7.95111 2.6427 6.46786C2.50919 5.99749 2.44244 5.76231 2.44141 5.41818C2.44028 5.03633 2.57475 4.51925 2.76176 4.18633C2.9303 3.88631 3.15098 3.66563 3.59233 3.22428L3.72369 3.09292C4.16656 2.65005 4.388 2.42861 4.62581 2.30833C5.09878 2.0691 5.65734 2.0691 6.1303 2.30833C6.36812 2.42861 6.58955 2.65005 7.03242 3.09292L7.19481 3.25531C7.48615 3.54665 7.63182 3.69231 7.72706 3.8388C8.08622 4.39121 8.08622 5.10336 7.72706 5.65576C7.63182 5.80225 7.48615 5.94792 7.19482 6.23925C7.09956 6.33451 7.05192 6.38214 7.01206 6.43782C6.87038 6.63568 6.82146 6.92256 6.88957 7.15619C6.90873 7.22193 6.93368 7.27389 6.98357 7.37781C7.08426 7.58753 7.1913 7.79496 7.30469 7.99982M9.31808 10.6817L9.35553 10.7193C10.3568 11.7206 11.4891 12.5113 12.6971 13.0913C12.801 13.1412 12.8529 13.1661 12.9187 13.1853C13.1523 13.2534 13.4392 13.2045 13.637 13.0628C13.6927 13.0229 13.7403 12.9753 13.8356 12.88C14.1269 12.5887 14.2726 12.443 14.4191 12.3478C14.9715 11.9886 15.6837 11.9886 16.2361 12.3478C16.3825 12.443 16.5282 12.5887 16.8196 12.88L16.9819 13.0424C17.4248 13.4853 17.6462 13.7067 17.7665 13.9446C18.0058 14.4175 18.0058 14.9761 17.7665 15.449C17.6462 15.6869 17.4248 15.9083 16.9819 16.3512L16.8506 16.4825C16.4092 16.9239 16.1886 17.1446 15.8885 17.3131C15.5556 17.5001 15.0385 17.6346 14.6567 17.6334C14.3126 17.6324 14.0774 17.5657 13.607 17.4322C11.0792 16.7147 8.69387 15.361 6.70388 13.371L6.66646 13.3334M17.4997 2.5001L2.49968 17.5001"
            stroke="currentColor"
            stroke-width="1.66667"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        {{ $t('VOICE_CALL.DECLINE_CALL') }}
      </button>
    </div>

    <!-- Call Controls (when in call) - Only for assigned agent -->
    <div
      v-else-if="showVoiceCallButton && isInCall && isConversationAssignedToMe"
      class="call-controls"
    >
      <div class="call-info">
        <span class="call-time">{{ callDuration }}</span>
      </div>

      <button
        class="control-btn mute-btn"
        :class="{ muted: isMuted }"
        @click="toggleMute"
      >
        {{ isMuted ? $t('VOICE_CALL.UNMUTE') : $t('VOICE_CALL.MUTE') }}
      </button>

      <button class="control-btn end-btn" @click="endVoiceCall">
        {{ $t('VOICE_CALL.END_CALL') }}
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.voice-call-button {
  display: inline-block;
  margin-left: var(--space-small);
  position: relative;
  z-index: 10;
  max-width: 400px;
  overflow: hidden;
}

.call-controls,
.call-options {
  display: flex;
  align-items: center;
  gap: 0px;
  padding: 0px;
  // background-color: var(--g-50);
  border-radius: 0px;
  // border: 1px solid var(--g-200);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  animation: slideIn 0.3s ease-out;
  max-width: fit-content;
  flex-shrink: 0;
}

.call-options.incoming-call {
  animation:
    incomingCallPulse 1.5s ease-in-out infinite,
    slideIn 0.3s ease-out;
  border: 2px solid #ff4444;
  box-shadow:
    0 0 20px rgba(255, 68, 68, 0.5),
    0 1px 3px rgba(0, 0, 0, 0.1);
  background: linear-gradient(45deg, #fff5f5, #ffe6e6);
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes incomingCallPulse {
  0% {
    transform: scale(1);
    box-shadow:
      0 0 20px rgba(255, 68, 68, 0.5),
      0 1px 3px rgba(0, 0, 0, 0.1);
  }
  50% {
    transform: scale(1.05);
    box-shadow:
      0 0 30px rgba(255, 68, 68, 0.8),
      0 1px 3px rgba(0, 0, 0, 0.1);
  }
  100% {
    transform: scale(1);
    box-shadow:
      0 0 20px rgba(255, 68, 68, 0.5),
      0 1px 3px rgba(0, 0, 0, 0.1);
  }
}

.call-info {
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 600;
  color: var(--g-700);
  min-width: 60px;
  padding: 8px;
  background-color: var(--g-100);
  text-align: center;
}

.call-time {
  font-family: 'Courier New', monospace;
  font-weight: bold;
  font-size: 12px;
}

.control-btn {
  padding: 6px 8px;
  min-width: 50px;
  border: 1px solid;
  border-radius: 0px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  letter-spacing: 0.3px;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
}

.mute-btn {
  background-color: var(--g-200);
  border-color: var(--g-300);
  color: var(--g-700);

  &:hover:not(:disabled) {
    background-color: var(--g-300);
    border-color: var(--g-400);
  }

  &.muted {
    background-color: var(--r-200);
    border-color: var(--r-300);
    color: var(--r-700);

    &:hover:not(:disabled) {
      background-color: var(--r-300);
      border-color: var(--r-400);
    }
  }
}

.end-btn {
  background-color: var(--r-200);
  border-color: var(--r-300);
  color: var(--r-700);

  &:hover:not(:disabled) {
    background-color: var(--r-300);
    border-color: var(--r-400);
  }
}

.join-btn {
  background-color: var(--g-200);
  border-color: var(--g-300);
  color: var(--g-700);
  text-transform: capitalize;

  &:hover:not(:disabled) {
    background-color: var(--g-300);
    border-color: var(--g-400);
  }
}

.incoming-call .join-btn {
  background-color: #4caf50;
  border-color: #45a049;
  color: white;
  font-weight: bold;
  animation: buttonPulse 2s ease-in-out infinite;

  &:hover:not(:disabled) {
    background-color: #45a049;
    border-color: #3d8b40;
    transform: translateY(-2px);
  }
}

.decline-btn {
  background-color: var(--r-200);
  border-color: var(--r-300);
  color: var(--r-700);
  text-transform: capitalize;

  &:hover:not(:disabled) {
    background-color: var(--r-300);
    border-color: var(--r-400);
  }
}

.incoming-call .decline-btn {
  background-color: #f44336;
  border-color: #d32f2f;
  color: white;
  font-weight: bold;

  &:hover:not(:disabled) {
    background-color: #d32f2f;
    border-color: #b71c1c;
    transform: translateY(-2px);
  }
}

@keyframes buttonPulse {
  0% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(76, 175, 80, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
  }
}
</style>
